import { useTranslation } from "react-i18next";
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Droppable,
  Draggable,
  DropResult,
  DragDropContext,
} from "react-beautiful-dnd";

import { GroupOptions } from "./GroupOptions";
import { DEFAULT_RISK_GROUP } from "./Risks";
import TextInput from "components/inputs/TextInput";
import { useConfirmation } from "context/Confirmation";
import TextArea from "components/inputs/TextAreaInput";
import { Risk as RiskComponent } from "components/Risk";
import { PrimaryButton } from "components/Button/PrimaryButton";
import Collapsible from "../../components/Collapsible/Collapsible";
import { RiskGroupping, Risk as RiskType } from "components/Risk/types";
import { CreateRiskOnDashboard } from "components/Risk/CreateRiskOnDashboard";
import { useBatchCreateTemplates } from "components/Risk/hooks/useBatchCreateTemplates";

interface RiskGroupsProps {
  grouping?: RiskGroupping[];
  risks: RiskType[];
  projectId: string;
  onGroupsReorder: (newGroups: RiskGroupping[]) => void;
}

export const RiskGroups: React.FC<RiskGroupsProps> = ({
  grouping,
  risks,
  projectId,
  onGroupsReorder,
}) => {
  const { t } = useTranslation();
  const { openConfirmation } = useConfirmation();
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const [groups, setGroups] = useState<RiskGroupping[]>(grouping || []);
  const [showOptionsForGroup, setShowOptionsForGroup] = useState<string | null>(
    null,
  );
  const optionsRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const createRiskButtonRef = useRef<{
    [key: string]: HTMLButtonElement | null;
  }>({});
  const createTemplatesInBatch = useBatchCreateTemplates();

  useEffect(() => {
    setGroups(grouping || []);
  }, [grouping]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showOptionsForGroup &&
        optionsRef.current &&
        buttonRef.current[showOptionsForGroup] &&
        !optionsRef.current.contains(event.target as Node) &&
        !buttonRef.current[showOptionsForGroup]?.contains(event.target as Node)
      ) {
        setShowOptionsForGroup(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showOptionsForGroup]);

  const onDragEnd = (result: DropResult) => {
    const { source, destination, type } = result;

    if (!destination) {
      return;
    }

    if (type === "GROUP") {
      // Prevent reordering of the default group
      if (destination.index === 0) {
        window.alert(t("risks.group.default_group_reorder"));
        return;
      }

      const newGroups = Array.from(groups);
      const [reorderedGroup] = newGroups.splice(source.index, 1);
      newGroups.splice(destination.index, 0, reorderedGroup);

      setGroups(newGroups);
      onGroupsReorder(newGroups);
    } else if (type === "RISK") {
      const sourceGroup = groups.find((g) => g.id === source.droppableId);
      const destGroup = groups.find((g) => g.id === destination.droppableId);

      if (sourceGroup && destGroup) {
        if (sourceGroup === destGroup) {
          const newMembers = Array.from(sourceGroup.members);
          const [movedRisk] = newMembers.splice(source.index, 1);
          newMembers.splice(destination.index, 0, movedRisk);

          const newGroups = groups.map((group) => {
            if (group.id === sourceGroup.id) {
              return { ...group, members: newMembers };
            }
            return group;
          });

          setGroups(newGroups);
          onGroupsReorder(newGroups);
        } else {
          const newSourceMembers = Array.from(sourceGroup.members);
          const newDestinationMembers = Array.from(destGroup.members);
          const [movedRisk] = newSourceMembers.splice(source.index, 1);
          newDestinationMembers.splice(destination.index, 0, movedRisk);

          const newGroups = groups.map((group) => {
            if (group.id === sourceGroup.id) {
              return { ...group, members: newSourceMembers };
            }

            if (group.id === destGroup.id) {
              return { ...group, members: newDestinationMembers };
            }

            return group;
          });

          setGroups(newGroups);
          onGroupsReorder(newGroups);
        }
      }
    }
  };

  const handleEdit = useCallback((group: RiskGroupping) => {
    setEditingGroup(group.id);
    setEditedName(group.label);
    setEditedDescription(group.description || "");
  }, []);

  const handleSave = useCallback(
    (group: RiskGroupping) => {
      const updatedGroup = {
        ...group,
        label: editedName,
        description: editedDescription,
      };

      const newGroups = groups.map((g) => {
        if (g.id === group.id) {
          return updatedGroup;
        }

        return g;
      });

      onGroupsReorder(newGroups);
      setGroups(newGroups);
      setEditingGroup(null);
    },
    [editedName, editedDescription, groups, onGroupsReorder, setGroups],
  );

  const handleDelete = (deletingGroupId: string) => {
    const deleteRiskGroup = (deletingGroupId: string) => {
      const members = groups.find(
        (group) => group.id === deletingGroupId,
      )?.members;

      const newGroups = groups
        .map((group) => {
          if (group.id === deletingGroupId) {
            return null;
          }

          if (group.id === DEFAULT_RISK_GROUP) {
            group.members = [...(group.members || []), ...(members || [])];
          }

          return group;
        })
        .filter(Boolean) as RiskGroupping[];

      onGroupsReorder(newGroups);
      setGroups(newGroups);
    };

    openConfirmation(() => deleteRiskGroup(deletingGroupId), {
      label: t("form.basic.label"),
      subline: t("risks.group.delete_confirmation_subline"),
    });
  };

  const handleGroupToTemplate = (groupId: string) => {
    const group = groups.find((g) => g.id === groupId)!;

    createTemplatesInBatch({
      group,
      projectCrn: projectId,
    });
  };

  const handleCancel = useCallback(() => {
    setEditingGroup(null);
  }, []);

  return (
    <div className="risk-groups">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="all-groups" type="GROUP">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {groups.map((group, groupIndex) => (
                <Draggable
                  key={group.id}
                  index={groupIndex}
                  draggableId={group.id || group.label}
                  isDragDisabled={group.id === DEFAULT_RISK_GROUP}
                >
                  {(providedGroup) => (
                    <div
                      ref={providedGroup.innerRef}
                      {...providedGroup.draggableProps}
                      {...providedGroup.dragHandleProps}
                      className="mb-8"
                    >
                      <div className="flex center items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-6 h-6 mr-2"
                        >
                          {group.id !== DEFAULT_RISK_GROUP && (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
                            />
                          )}
                        </svg>

                        <div
                          className="bg-white shadow sm:rounded-lg w-full"
                          style={{ overflow: "visible" }}
                        >
                          <div className="bg-white px-4 py-5 border-b border-gray-200 sm:px-6">
                            <div className="-ml-4 -mt-4 flex justify-between items-center flex-wrap sm:flex-nowrap">
                              <div className="ml-4 mt-4 w-full">
                                <div className="flex items-center mb-2 justify-between">
                                  {editingGroup === group.id ? (
                                    <div className="text-sm text-gray-500">
                                      <b>{t("risks.group.group")}:</b>{" "}
                                      <TextInput
                                        id="label"
                                        required
                                        defaultValue={group.label}
                                        uncontrolled
                                        // @ts-ignore
                                        onChange={(e) =>
                                          setEditedName(e.target.value)
                                        }
                                      />
                                    </div>
                                  ) : (
                                    <h3 className="text-lg leading-6 font-medium text-gray-900 mr-2">
                                      <b>
                                        {t("risks.group.group")} #
                                        {groupIndex + 1}:
                                      </b>{" "}
                                      {group.label}
                                    </h3>
                                  )}

                                  {group.id !== DEFAULT_RISK_GROUP && (
                                    <div
                                      className="text-right flex justify-center items-center float-right"
                                      style={{ overflow: "visible" }}
                                    >
                                      {(!editingGroup ||
                                        editingGroup !== group.id) && (
                                        <div
                                          className="relative flex justify-end items-center"
                                          style={{ overflow: "visible" }}
                                        >
                                          <button
                                            type="button"
                                            className="w-8 h-8 inline-flex items-center justify-center text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                                            aria-expanded="false"
                                            aria-haspopup="true"
                                            ref={(el) =>
                                              (buttonRef.current[group.id] = el)
                                            }
                                            onClick={() => {
                                              setShowOptionsForGroup(
                                                showOptionsForGroup === group.id
                                                  ? null
                                                  : group.id,
                                              );
                                            }}
                                          >
                                            <span className="sr-only">
                                              Open options
                                            </span>
                                            <svg
                                              className="w-5 h-5"
                                              xmlns="http://www.w3.org/2000/svg"
                                              viewBox="0 0 20 20"
                                              fill="currentColor"
                                              aria-hidden="true"
                                            >
                                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                            </svg>
                                          </button>
                                          {showOptionsForGroup === group.id && (
                                            <div ref={optionsRef}>
                                              <GroupOptions
                                                onClose={() =>
                                                  setShowOptionsForGroup(null)
                                                }
                                                onEdit={() => handleEdit(group)}
                                                onDelete={() =>
                                                  handleDelete(group.id)
                                                }
                                                onCreateRisk={() => {
                                                  createRiskButtonRef.current[
                                                    group.id
                                                  ]?.click();
                                                }}
                                                onGroupToTemplate={() =>
                                                  handleGroupToTemplate(
                                                    group.id,
                                                  )
                                                }
                                                group={group}
                                              />
                                            </div>
                                          )}
                                          {/* Hidden button to trigger CreateRiskOnDashboard modal */}
                                          <div style={{ display: "none" }}>
                                            <CreateRiskOnDashboard
                                              projectCRN={projectId}
                                              defaultGroupId={group.id}
                                              button={(props) => (
                                                <button
                                                  ref={(el) =>
                                                    (createRiskButtonRef.current[
                                                      group.id
                                                    ] = el)
                                                  }
                                                  onClick={props.onClick}
                                                />
                                              )}
                                            />
                                          </div>
                                        </div>
                                      )}

                                      {editingGroup === group.id && (
                                        <>
                                          <a
                                            className="px-6 text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
                                            onClick={handleCancel}
                                          >
                                            {t("risks.group.cancel")}
                                          </a>
                                          <PrimaryButton
                                            onClick={() => handleSave(group)}
                                          >
                                            {t("risks.group.save")}
                                          </PrimaryButton>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="mt-1 text-sm text-gray-500">
                                  <div>
                                    <span>
                                      <b>
                                        {t("form.mitigations.description")}:
                                      </b>{" "}
                                      {editingGroup === group.id ? (
                                        <TextArea
                                          required
                                          id="description"
                                          name="description"
                                          label=""
                                          defaultValue={group.description}
                                          onChange={(e) =>
                                            setEditedDescription(e.target.value)
                                          }
                                        ></TextArea>
                                      ) : (
                                        group.description
                                      )}
                                    </span>
                                  </div>
                                  <Droppable
                                    droppableId={group.id || group.label}
                                    type="RISK"
                                  >
                                    {(providedRisks) => (
                                      <div
                                        {...providedRisks.droppableProps}
                                        ref={providedRisks.innerRef}
                                      >
                                        <Collapsible
                                          open={group.id === DEFAULT_RISK_GROUP}
                                          label={t(
                                            "risks.group.risks_in_group",
                                            { group: group.label },
                                          )}
                                        >
                                          {group.members?.map(
                                            (riskId) =>
                                              risks.find(
                                                ({ resource_id }) =>
                                                  resource_id === riskId,
                                              )!,
                                          ).length ? (
                                            <>
                                              {group.members
                                                ?.map(
                                                  (riskId) =>
                                                    risks.find(
                                                      ({ resource_id }) =>
                                                        resource_id === riskId,
                                                    )!,
                                                )
                                                .filter(Boolean)
                                                .map((risk, riskIndex) => (
                                                  <Draggable
                                                    key={risk.resource_id}
                                                    draggableId={risk.resource_id.toString()}
                                                    index={riskIndex}
                                                  >
                                                    {(providedRisk) => (
                                                      <div
                                                        ref={
                                                          providedRisk.innerRef
                                                        }
                                                        {...providedRisk.draggableProps}
                                                        {...providedRisk.dragHandleProps}
                                                        className="flex items-center my-2"
                                                      >
                                                        <svg
                                                          xmlns="http://www.w3.org/2000/svg"
                                                          fill="none"
                                                          viewBox="0 0 24 24"
                                                          strokeWidth={1.5}
                                                          stroke="currentColor"
                                                          className="w-6 h-6 mr-2"
                                                        >
                                                          <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
                                                          />
                                                        </svg>
                                                        <RiskComponent
                                                          risk={risk}
                                                          projectId={projectId}
                                                          idx={riskIndex}
                                                        />
                                                      </div>
                                                    )}
                                                  </Draggable>
                                                ))}
                                              {providedRisks.placeholder}
                                            </>
                                          ) : (
                                            <>
                                              <div className="relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                                                {providedRisks.placeholder}
                                                <svg
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  fill="none"
                                                  viewBox="0 0 24 24"
                                                  strokeWidth={1.5}
                                                  stroke="currentColor"
                                                  className="mx-auto h-8 w-8 text-gray-400"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M13.5 16.875h3.375m0 0h3.375m-3.375 0V13.5m0 3.375v3.375M6 10.5h2.25a2.25 2.25 0 0 0 2.25-2.25V6a2.25 2.25 0 0 0-2.25-2.25H6A2.25 2.25 0 0 0 3.75 6v2.25A2.25 2.25 0 0 0 6 10.5Zm0 9.75h2.25A2.25 2.25 0 0 0 10.5 18v-2.25a2.25 2.25 0 0 0-2.25-2.25H6a2.25 2.25 0 0 0-2.25 2.25V18A2.25 2.25 0 0 0 6 20.25Zm9.75-9.75H18a2.25 2.25 0 0 0 2.25-2.25V6A2.25 2.25 0 0 0 18 3.75h-2.25A2.25 2.25 0 0 0 13.5 6v2.25a2.25 2.25 0 0 0 2.25 2.25Z"
                                                  />
                                                </svg>
                                                <span className="mt-2 block text-sm font-semibold text-gray-400">
                                                  {t("risks.group.dnd_hint")}
                                                </span>
                                              </div>
                                            </>
                                          )}
                                        </Collapsible>
                                      </div>
                                    )}
                                  </Droppable>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};
